import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import usageChartSvg from "../theme/icons/usage-chart.svg?raw";

type UsageData = Awaited<ReturnType<typeof api.usage>>;
type RollupRow = UsageData["rollup"][number];

// Rollup buckets are the worker's UTC calendar dates (current_date in the
// SQL), so "Today"/"Yesterday" and the formatted label are computed on the
// UTC calendar too — a bucket's name must never disagree with how it was
// binned. The raw ISO date stays on the cell's title for the exact value.
function dayLabel(day: string): string {
  const iso = day.slice(0, 10);
  const now = Date.now();
  if (iso === new Date(now).toISOString().slice(0, 10)) return "Today";
  if (iso === new Date(now - 86_400_000).toISOString().slice(0, 10))
    return "Yesterday";
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

// MCP traffic is metered as `mcp:<tool>` (see worker metering/channels.ts);
// the Channel column already says MCP, so the row shows just the tool name.
// The full route class stays on the <code>'s title.
function routeLabel(routeClass: string): string {
  return routeClass.startsWith("mcp:") ? routeClass.slice(4) : routeClass;
}

// One group per day (server order — most recent first), routes sorted
// biggest-consumer-first within the day so the table scans top-down.
function groupByDay(rollup: RollupRow[]) {
  const days = new Map<string, RollupRow[]>();
  for (const r of rollup) {
    const key = r.day.slice(0, 10);
    const rows = days.get(key);
    if (rows) rows.push(r);
    else days.set(key, [r]);
  }
  return Array.from(days.entries()).map(([day, rows]) => ({
    day,
    rows: rows.slice().sort((a, b) => Number(b.units) - Number(a.units)),
    total: rows.reduce((s, r) => s + Number(r.units), 0),
  }));
}

// Sketch of the loaded page (stat tiles + a table card) while the first
// fetch resolves — same convention as DashboardSkeleton: decorative blocks
// are aria-hidden, the sr-only line carries the spoken cue.
function UsageSkeleton() {
  return (
    <div role="status">
      <span className="sr-only">Loading usage…</span>
      <div className="stat-row" aria-hidden>
        {[0, 1, 2].map((i) => (
          <div className="card stat-tile" key={i}>
            <span className="skeleton" style={{ width: "40%" }} />
            <span
              className="skeleton lg"
              style={{ width: "30%", margin: "var(--space-2) 0" }}
            />
            <span className="skeleton" style={{ width: "55%" }} />
          </div>
        ))}
      </div>
      <div className="card" aria-hidden>
        {["70%", "55%", "62%"].map((w, i) => (
          <span
            className="skeleton"
            key={i}
            style={{ width: w, marginTop: i === 0 ? 0 : "var(--space-4)" }}
          />
        ))}
      </div>
    </div>
  );
}

// One quiet line locating this usage against the plan: which tier, what's
// included, where to change it. The entitlement default is plan='locked'
// with 0 included units (pre-checkout / lapsed) — for that shape a badge
// saying "locked" reads like an error, so point at Billing instead.
function PlanLine({ ent }: { ent: UsageData["entitlement"] }) {
  if (!ent || ent.plan === "locked") {
    return (
      <p className="muted" style={{ marginTop: 0 }}>
        No active subscription — start one on{" "}
        <Link to="/billing">Billing</Link> to get a monthly unit allowance.
      </p>
    );
  }
  const included = Number(ent.included_units);
  return (
    <p className="muted" style={{ marginTop: 0 }}>
      On the <span className="badge">{ent.plan}</span> plan
      {included > 0 && (
        <> · {included.toLocaleString()} units included per month</>
      )}
      {/* status is 'active' throughout the normal lifecycle (locked is a
          read_model, not a suspension) — anything else is exceptional and
          worth surfacing verbatim. */}
      {ent.status !== "active" && (
        <>
          {" "}
          ·{" "}
          <span className="pill warn">
            <span className="pill-dot" aria-hidden />
            {ent.status}
          </span>
        </>
      )}{" "}
      — manage it on <Link to="/billing">Billing</Link>.
    </p>
  );
}

export function Usage() {
  // null = not yet fetched (same sentinel convention as ConnectedApps /
  // ApiKeys); error tracked separately so a failed fetch is never mistaken
  // for "no usage" — showing the getting-started empty state on a
  // transient error would tell an active account it used nothing.
  const [data, setData] = useState<UsageData | null>(null);
  const [error, setError] = useState(false);

  const load = () => {
    setError(false);
    api
      .usage()
      .then(setData)
      .catch(() => setError(true));
  };
  useEffect(() => {
    load();
  }, []);

  let body: React.ReactNode;
  if (data === null && error) {
    body = (
      <div className="card">
        <p className="muted">
          Couldn’t load your usage — usually a transient network hiccup.
        </p>
        <button className="btn" onClick={load}>
          Retry
        </button>
      </div>
    );
  } else if (data === null) {
    body = <UsageSkeleton />;
  } else if (data.rollup.length === 0) {
    body = (
      <>
        <div className="card agents-empty">
          <span
            className="hero-mark"
            aria-hidden
            dangerouslySetInnerHTML={{ __html: usageChartSvg }}
          />
          <h2>No usage in the last 30 days</h2>
          <p className="muted">
            Connect an agent or call the REST API and every unit shows up
            here within a few minutes, broken down by day and endpoint.
          </p>
          <div
            style={{
              display: "flex",
              gap: "var(--space-3)",
              justifyContent: "center",
              marginTop: "var(--space-4)",
            }}
          >
            <Link to="/connected-apps" className="btn primary">
              Connect an agent
            </Link>
            <Link to="/keys" className="btn secondary">
              Create an API key
            </Link>
          </div>
        </div>
        <PlanLine ent={data.entitlement} />
      </>
    );
  } else {
    const total = data.rollup.reduce((s, r) => s + Number(r.units), 0);
    const included = Number(data.entitlement?.included_units ?? 0);
    // Coverage tiers and locked accounts carry included_units = 0 — no
    // allowance to meter against, so the tile shows the plain total.
    const pct = included > 0 ? Math.round((total / included) * 100) : null;
    const groups = groupByDay(data.rollup);
    body = (
      <>
        <div className="stat-row">
          <div className="card stat-tile">
            <div className="eyebrow">Units · last 30 days</div>
            <div className="stat-value">{total.toLocaleString()}</div>
            <div className="muted" style={{ fontSize: 12 }}>
              {pct !== null
                ? `${pct}% of ${included.toLocaleString()} included / mo`
                : "units used"}
            </div>
            {/* Decorative — the line above carries the same numbers as
                text. Fill colour escalates as the allowance runs down. */}
            {pct !== null && (
              <div className="meter" aria-hidden>
                <span
                  className={`meter-fill${
                    pct >= 100 ? " danger" : pct >= 80 ? " warn" : ""
                  }`}
                  style={{ width: `${Math.min(100, pct)}%` }}
                />
              </div>
            )}
          </div>
          <Link to="/keys" className="card stat-tile">
            <div className="eyebrow">REST API</div>
            <div className="stat-value">
              {data.by_channel.rest.toLocaleString()}
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              {data.by_channel.rest === 0
                ? "create an API key →"
                : "calls with your API keys"}
            </div>
          </Link>
          <Link to="/connected-apps" className="card stat-tile">
            <div className="eyebrow">Agents · MCP</div>
            <div className="stat-value">
              {data.by_channel.mcp.toLocaleString()}
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              {data.by_channel.mcp === 0
                ? "connect Claude or ChatGPT →"
                : "tool calls by your agents"}
            </div>
          </Link>
        </div>

        <PlanLine ent={data.entitlement} />

        {pct !== null && pct >= 100 && (
          <p className="error">
            Your last 30 days are above your monthly included units — manage
            your plan on <Link to="/billing">Billing</Link>.
          </p>
        )}

        <div className="card">
          <h2>Daily breakdown</h2>
          <table>
            <thead>
              <tr>
                <th>Day</th>
                <th>Endpoint</th>
                <th>Channel</th>
                <th className="num">Units</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) =>
                g.rows.map((r, i) => (
                  <tr key={`${g.day}-${r.route_class}`}>
                    {i === 0 && (
                      <td
                        className="day-cell"
                        rowSpan={g.rows.length}
                        title={g.day}
                      >
                        <div>{dayLabel(g.day)}</div>
                        {g.rows.length > 1 && (
                          <div className="muted" style={{ fontSize: 12 }}>
                            {g.total.toLocaleString()} total
                          </div>
                        )}
                      </td>
                    )}
                    <td>
                      <code title={r.route_class}>
                        {routeLabel(r.route_class)}
                      </code>
                    </td>
                    <td>
                      <span className="badge">
                        {r.route_class.startsWith("mcp:") ? "MCP" : "REST"}
                      </span>
                    </td>
                    <td className="num">{Number(r.units).toLocaleString()}</td>
                  </tr>
                )),
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>Total · last 30 days</td>
                <td className="num">{total.toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </>
    );
  }

  return (
    <div className="main">
      <div className="page-header">
        <h1>Usage</h1>
      </div>
      <p className="page-sub">
        Every billable call your organisation made in the last 30 days —
        REST API calls and agent (MCP) tool calls together. Counted from the
        billing ledger, so these are the same numbers your plan allowance is
        measured against.
      </p>
      {body}
    </div>
  );
}
